import React, { createContext, useContext, useState, useMemo, useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import { CategoryQueryContext } from 'src/components/CategoryListingPage/CategoryQueryProvider';

export type PopupPageInfo = {
    type: 'category' | 'product' | 'other';
    entityId: number | null;
    categoryId: number | null;
    parentCategoryIds: number[];
};

const defaultPageInfo: PopupPageInfo = {
    type: 'other',
    entityId: null,
    categoryId: null,
    parentCategoryIds: [],
};

type PopupPageContextType = {
    pageInfo: PopupPageInfo;
    setPageInfo: (info: PopupPageInfo) => void;
};

const PopupPageContext = createContext<PopupPageContextType>({
    pageInfo: defaultPageInfo,

    setPageInfo: () => {},
});

export const PopupPageProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
    const [pageInfo, setPageInfo] = useState<PopupPageInfo>(defaultPageInfo);
    const { pathname } = useLocation();
    const prevPathname = useRef(pathname);

    useEffect(() => {
        if (prevPathname.current !== pathname) {
            prevPathname.current = pathname;
            setPageInfo(defaultPageInfo);
        }
    }, [pathname]);

    const value = useMemo(() => ({ pageInfo, setPageInfo }), [pageInfo]);

    return <PopupPageContext.Provider value={value}>{children}</PopupPageContext.Provider>;
};

export function usePopupPageInfo() {
    return useContext(PopupPageContext);
}

export function useSetPopupPageCategory(categoryId: number | string, parentCategoryIds: number[]) {
    const { setPageInfo } = useContext(PopupPageContext);
    const numericId = Number(categoryId);

    useEffect(() => {
        if (numericId > 0) {
            setPageInfo({
                type: 'category',
                entityId: numericId,
                categoryId: numericId,
                parentCategoryIds,
            });
        }
    }, [numericId, parentCategoryIds, setPageInfo]);
}

/**
 * Drop this inside any `CategoryQueryProvider` to auto-sync category info
 * to the popup page context (useful for landing pages, brand pages, etc.).
 */
export const PopupCategoryBridge: React.FC = () => {
    const { category_id, parentCategoryIds } = useContext(CategoryQueryContext);
    useSetPopupPageCategory(category_id, parentCategoryIds);
    return null;
};

export function useSetPopupPageProduct(productId: number | string, productCategoryId: number | string | null) {
    const { setPageInfo } = useContext(PopupPageContext);
    const numericProductId = Number(productId);
    const numericCategoryId = productCategoryId !== null ? Number(productCategoryId) : null;

    useEffect(() => {
        if (numericProductId > 0) {
            setPageInfo({
                type: 'product',
                entityId: numericProductId,
                categoryId: numericCategoryId,
                parentCategoryIds: [],
            });
        }
    }, [numericProductId, numericCategoryId, setPageInfo]);
}
